import dayjs, { type Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { EntityType } from '../types';
import type { RepositoryEntriesRowDto } from '../services/repositoryEntries';

dayjs.extend(isoWeek);

export const CHART_COUNT_METRIC_VALUE = '__count__';

export type ChartTimeScale = 'D' | 'W' | 'M';

export type ChartPresentationVariant = 'line' | 'bar' | 'heatmap';

export function isChartDateLikePropertyType(entityType: EntityType | undefined): boolean {
	return entityType === EntityType.PropertyDefinitionDate
		|| entityType === EntityType.PropertyDefinitionDatetime;
}

function parseXDate(raw: unknown): Dayjs | null {
	if (raw === null || raw === undefined || raw === '') {
		return null;
	}
	const d = dayjs(String(raw).trim());
	return d.isValid() ? d : null;
}

function bucketStart(d: Dayjs, scale: ChartTimeScale): Dayjs {
	if (scale === 'D') {
		return d.startOf('day');
	}
	if (scale === 'W') {
		return d.startOf('isoWeek');
	}
	return d.startOf('month');
}

function bucketKeyFromStart(start: Dayjs, scale: ChartTimeScale): string {
	if (scale === 'D' || scale === 'W') {
		return start.format('YYYY-MM-DD');
	}
	return start.format('YYYY-MM');
}

type ChartRow = Pick<RepositoryEntriesRowDto, 'data'>;

/**
 * Optional inclusive YYYY-MM-DD bounds from the resolved chart filter for the x-axis slug
 * (same shape as API `dataFilter`). When time bucketing is on, these override the series
 * window edges when present instead of only min/max from rows.
 */
export type TimeBucketFilterBounds = { start?: string; end?: string };

export type CanvasChartSeriesPoint = { xValue: string; metricValue: number | null };

/**
 * Builds chart series points for canvas charts.
 * When `useTimeBuckets` is true, values are summed (or counted) per time bucket and every bucket
 * between min and max (or filter bounds) is included. Use `missingBucketMetric: 'null'` for line
 * charts so Recharts can reserve x-axis space while skipping segments over empty buckets.
 */
export function buildCanvasChartSeries(params: {
	rows: ChartRow[];
	metricKey: string;
	xAxisKey: string;
	timeScale: ChartTimeScale | null;
	useTimeBuckets: boolean;
	/** Resolved filter bounds for `xAxisKey` (e.g. from `parseResolvedChartDataFilterBoundsForSlug`). */
	timeBucketRangeFromFilter?: TimeBucketFilterBounds | null;
	/** Value for buckets in range with no data. `'zero'` for bar/heatmap; `'null'` for line charts. */
	missingBucketMetric?: 'zero' | 'null';
}): CanvasChartSeriesPoint[] {
	const {
		rows,
		metricKey,
		xAxisKey,
		timeScale,
		useTimeBuckets,
		timeBucketRangeFromFilter,
		missingBucketMetric = 'zero',
	} = params;
	if (!metricKey || !xAxisKey) {
		return [];
	}

	if (!useTimeBuckets || !timeScale) {
		if (metricKey === CHART_COUNT_METRIC_VALUE) {
			const countsByXAxis = new Map<string, number>();
			rows.forEach((entry) => {
				const xValueRaw = entry.data?.[xAxisKey];
				if (xValueRaw === null || xValueRaw === undefined || xValueRaw === '') {
					return;
				}
				const xValue = String(xValueRaw);
				countsByXAxis.set(xValue, (countsByXAxis.get(xValue) ?? 0) + 1);
			});
			return Array.from(countsByXAxis.entries()).map(([xValue, metricValue]) => ({
				xValue,
				metricValue,
			}));
		}

		return rows
			.map((entry) => {
				const metricRaw = entry.data?.[metricKey];
				const metricValue = Number(metricRaw);
				if (!Number.isFinite(metricValue)) {
					return null;
				}
				const xValueRaw = entry.data?.[xAxisKey];
				if (xValueRaw === null || xValueRaw === undefined || xValueRaw === '') {
					return null;
				}
				return {
					xValue: String(xValueRaw),
					metricValue,
				};
			})
			.filter((row): row is CanvasChartSeriesPoint & { metricValue: number } => row !== null);
	}

	const totals = new Map<string, number>();
	const bucketStartsSeen: Dayjs[] = [];

	rows.forEach((entry) => {
		const xParsed = parseXDate(entry.data?.[xAxisKey]);
		if (!xParsed) {
			return;
		}
		const start = bucketStart(xParsed, timeScale);
		const key = bucketKeyFromStart(start, timeScale);
		bucketStartsSeen.push(start);

		if (metricKey === CHART_COUNT_METRIC_VALUE) {
			totals.set(key, (totals.get(key) ?? 0) + 1);
			return;
		}
		const metricRaw = entry.data?.[metricKey];
		const metricValue = Number(metricRaw);
		if (!Number.isFinite(metricValue)) {
			return;
		}
		totals.set(key, (totals.get(key) ?? 0) + metricValue);
	});

	const dataMinBucket = bucketStartsSeen.length
		? bucketStartsSeen.reduce((a, b) => (a.isBefore(b) ? a : b))
		: null;
	const dataMaxBucket = bucketStartsSeen.length
		? bucketStartsSeen.reduce((a, b) => (a.isAfter(b) ? a : b))
		: null;

	const fb = timeBucketRangeFromFilter;
	let fromFilterMin: Dayjs | null = null;
	let fromFilterMax: Dayjs | null = null;
	if (fb?.start) {
		const d = dayjs(fb.start.slice(0, 10));
		if (d.isValid()) {
			fromFilterMin = bucketStart(d, timeScale);
		}
	}
	if (fb?.end) {
		const d = dayjs(fb.end.slice(0, 10));
		if (d.isValid()) {
			fromFilterMax = bucketStart(d, timeScale);
		}
	}

	let rangeMin: Dayjs | null = fromFilterMin ?? dataMinBucket;
	let rangeMax: Dayjs | null = fromFilterMax ?? dataMaxBucket;

	if (rangeMin && !rangeMax) {
		rangeMax = rangeMin;
	}
	if (rangeMax && !rangeMin) {
		rangeMin = rangeMax;
	}
	if (!rangeMin || !rangeMax) {
		return [];
	}
	if (rangeMin.isAfter(rangeMax)) {
		const t = rangeMin;
		rangeMin = rangeMax;
		rangeMax = t;
	}

	const out: CanvasChartSeriesPoint[] = [];
	let cursor = rangeMin;

	for (;;) {
		const start = bucketStart(cursor, timeScale);
		const key = bucketKeyFromStart(start, timeScale);
		const metricValue = totals.has(key)
			? totals.get(key)! // eslint-disable-line @typescript-eslint/no-non-null-assertion
			: (missingBucketMetric === 'null' ? null : 0);
		out.push({
			xValue: key,
			metricValue,
		});
		if (start.valueOf() >= rangeMax.valueOf()) {
			break;
		}
		const unit = timeScale === 'D' ? 'day' : timeScale === 'W' ? 'week' : 'month';
		cursor = start.add(1, unit);
	}

	return out;
}

/** Optional reference band for line/bar charts (Y-axis, metric units). */
export type ChartTargetReference =
	| { kind: 'none' }
	| { kind: 'flat'; y: number }
	| {
		kind: 'segment';
		segment: [{ x: string; y: number }, { x: string; y: number }];
	};

function finiteChartTargetNumber(v: unknown): number | null {
	if (v === null || v === undefined || v === '') {
		return null;
	}
	const n = typeof v === 'number' ? v : Number(v);
	return Number.isFinite(n) ? n : null;
}

/**
 * Flat reference when only target A is set, or both targets are equal.
 * Angled segment when both differ, or when only target B is set (target A treated as 0).
 */
export function resolveChartTargetReference(params: {
	targetA: unknown;
	targetB: unknown;
	chartData: readonly { xValue: string }[];
}): ChartTargetReference {
	const { targetA, targetB, chartData } = params;
	if (chartData.length === 0) {
		return { kind: 'none' };
	}
	const a = finiteChartTargetNumber(targetA);
	const b = finiteChartTargetNumber(targetB);
	if (a === null && b === null) {
		return { kind: 'none' };
	}
	const x0 = chartData[0].xValue;
	const x1 = chartData[chartData.length - 1].xValue;

	if (a === null && b !== null) {
		if (b === 0) {
			return { kind: 'flat', y: 0 };
		}
		return {
			kind: 'segment',
			segment: [{ x: x0, y: 0 }, { x: x1, y: b }],
		};
	}

	if (a !== null && b === null) {
		return { kind: 'flat', y: a };
	}

	if (a !== null && b !== null) {
		if (a === b) {
			return { kind: 'flat', y: a };
		}
		return {
			kind: 'segment',
			segment: [{ x: x0, y: a }, { x: x1, y: b }],
		};
	}

	return { kind: 'none' };
}
